import type {
  DraftTransaction,
  SplitTransactionRequest,
  VATRate,
  VatAssignment,
} from '@finapp/types';
import type React from 'react';
import { useEffect, useState } from 'react';
import { vatService } from '../services/vatService';

interface SplitModalProps {
  transaction: DraftTransaction;
  onSplit: (splits: SplitTransactionRequest) => void;
  onClose: () => void;
}

const SplitModal: React.FC<SplitModalProps> = ({ transaction, onSplit, onClose }) => {
  const [splitAmounts, setSplitAmounts] = useState<number[]>([]);
  const [vatAssignments, setVatAssignments] = useState<VatAssignment[]>([]);
  const [vatRates, setVatRates] = useState<VATRate[]>([]);
  const [loading, setLoading] = useState(false);

  // Load VAT rates on mount
  useEffect(() => {
    vatService
      .getVatRates()
      .then((rates) => setVatRates(rates))
      .catch((error) => console.error('Failed to fetch VAT rates:', error));
  }, []);

  const addSplitAmount = () => {
    setSplitAmounts([...splitAmounts, 0]);
    setVatAssignments([...vatAssignments, { has_vat: false, vat_id: null }]);
  };

  const removeSplitAmount = (index: number) => {
    setSplitAmounts(splitAmounts.filter((_, i) => i !== index));
    setVatAssignments(vatAssignments.filter((_, i) => i !== index));
  };

  const updateSplitAmount = (index: number, amount: number) => {
    const newAmounts = [...splitAmounts];
    newAmounts[index] = amount;
    setSplitAmounts(newAmounts);
  };

  const updateVatAssignment = (
    index: number,
    field: keyof VatAssignment,
    value: VatAssignment[keyof VatAssignment]
  ) => {
    const newAssignments = [...vatAssignments];
    const currentAssignment = newAssignments[index] || {
      has_vat: false,
      vat_id: null,
    };
    newAssignments[index] = { ...currentAssignment, [field]: value };
    setVatAssignments(newAssignments);
  };

  const totalSplitAmount = splitAmounts.reduce((sum, amount) => sum + amount, 0);
  const totalMatches = Math.abs(totalSplitAmount - transaction.amount) < 0.01;
  const canSplit =
    totalMatches && splitAmounts.length >= 2 && splitAmounts.every((amount) => amount > 0);

  const handleSplit = async () => {
    if (!canSplit) return;

    setLoading(true);
    try {
      await onSplit({
        split_amounts: splitAmounts,
        vat_assignments: vatAssignments,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-4">
          Split Transaction: {transaction.heading} (€{transaction.amount})
        </h3>

        {/* Split Amounts */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">Split Amounts</label>
          <div className="space-y-2">
            {splitAmounts.map((amount, index) => (
              <div key={index} className="flex items-center space-x-2">
                <input
                  type="number"
                  value={amount || ''}
                  onChange={(e) => updateSplitAmount(index, parseFloat(e.target.value) || 0)}
                  className="w-32 px-3 py-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  step="0.01"
                  placeholder="0.00"
                  min="0"
                />
                <span className="text-sm text-gray-500">€</span>
                <button
                  onClick={() => removeSplitAmount(index)}
                  className="text-red-500 hover:text-red-700 text-sm px-2 py-1 rounded hover:bg-red-50"
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              onClick={addSplitAmount}
              className="text-blue-500 hover:text-blue-700 text-sm px-2 py-1 rounded hover:bg-blue-50"
            >
              + Add Split
            </button>
          </div>

          {/* Validation */}
          <div className="mt-2 text-sm">
            <span className={totalMatches ? 'text-green-600' : 'text-red-600'}>
              Total: €{totalSplitAmount.toFixed(2)} / €{transaction.amount}
            </span>
            {!totalMatches && (
              <div className="text-red-600 text-xs mt-1">
                Split amounts must equal the original transaction amount
              </div>
            )}
          </div>
        </div>

        {/* VAT Assignment for each split */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">VAT Assignment</label>
          {splitAmounts.map((amount, index) => (
            <div key={index} className="mb-3 p-3 border rounded bg-gray-50">
              <div className="font-medium mb-2">
                Split {index + 1}: €{amount}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Has VAT</label>
                  <input
                    type="checkbox"
                    checked={vatAssignments[index]?.has_vat || false}
                    onChange={(e) => updateVatAssignment(index, 'has_vat', e.target.checked)}
                    className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">VAT Rate</label>
                  <select
                    value={vatAssignments[index]?.vat_id || ''}
                    onChange={(e) =>
                      updateVatAssignment(
                        index,
                        'vat_id',
                        e.target.value ? parseInt(e.target.value, 10) : null
                      )
                    }
                    className="w-full px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    disabled={!vatAssignments[index]?.has_vat}
                  >
                    <option value="">Select VAT Rate</option>
                    {vatRates.map((rate) => (
                      <option key={rate.id} value={rate.id}>
                        {rate.rate}% - {rate.description}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-50"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSplit}
            disabled={!canSplit || loading}
            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {loading && (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            <span>{loading ? 'Splitting...' : 'Split Transaction'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SplitModal;
